import { Link } from 'react-router-dom';

import { Route42InlineMarkdown, Route42Markdown } from '../../../components/ui/Route42Markdown';

import type {
  MachineMemoryConflictDto,
  MachineMemoryGapDto,
  MachineMemoryInsightDto,
  MachineMemoryInvestigationKnowledgeDto,
  MachineMemoryKnowledgeEvidenceDto,
  MachineMemoryItem,
  MachineMemoryRelationshipDto,
  MachineMemoryRelationshipEvidenceDto,
  MachineMemoryThemeDto,
  MachineWorkspaceDocumentDto,
  MachineWorkspaceInvestigationDto,
} from '../types/machine-memory';
import { evidenceStrengthLabel, humanMemoryType } from '../types/machine-memory';

export function MemoryDetailPanel({
  machineId,
  item,
  documents,
  investigations,
  onAsk,
  onInvestigate,
  onClose,
}: {
  machineId: string;
  item: MachineMemoryItem;
  documents: MachineWorkspaceDocumentDto[];
  investigations: MachineWorkspaceInvestigationDto[];
  onAsk?: (prompt: string) => void;
  onInvestigate?: (title: string, objective: string) => void;
  onClose: () => void;
}) {
  const detail = normalize(item);
  const sourceInvestigation = detail.investigationId
    ? investigations.find((value) => value.investigation_id === detail.investigationId)
    : undefined;
  const sourceDocuments = detail.documentIds
    .map((id) => documents.find((document) => document.document_id === id))
    .filter((document): document is MachineWorkspaceDocumentDto => Boolean(document));

  return (
    <aside className="r42-memory-detail-panel" aria-label="Machine Memory detail">
      <header className="r42-memory-detail-panel__head">
        <div>
          <span className="r42-eyebrow">Machine Memory</span>
          <h2>{detail.title}</h2>
        </div>
        <button className="r42-icon-button" type="button" aria-label="Close Machine Memory detail" onClick={onClose}>
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      </header>

      <div className="r42-memory-detail-panel__body">
        <div className="r42-memory-detail-meta">
          <span>{detail.kindLabel}</span>
          {detail.strength ? <b className={`r42-evidence-strength r42-evidence-strength--${detail.strength.toLocaleLowerCase()}`}>{detail.strength}</b> : null}
        </div>

        <section>
          <span className="r42-eyebrow">What Route42 currently knows</span>
          <Route42Markdown className="r42-memory-detail-statement" content={detail.statement} />
        </section>

        {detail.investigationId ? (
          <section>
            <span className="r42-eyebrow">Established by Investigation</span>
            {sourceInvestigation ? (
              <Link className="r42-memory-investigation-source" to={`/machines/${machineId}/investigations/${sourceInvestigation.investigation_id}`}>
                <i className="bi bi-search" aria-hidden="true" />
                <span><strong>{sourceInvestigation.title || 'Completed Investigation'}</strong><small>{detail.establishedAt ? `Established ${formatDate(detail.establishedAt)}` : 'Human-confirmed at Investigation closure'}</small></span>
                <i className="bi bi-arrow-up-right" aria-hidden="true" />
              </Link>
            ) : (
              <p>{detail.establishedAt ? `Established ${formatDate(detail.establishedAt)} through a human-confirmed Investigation closure.` : 'Established through a human-confirmed Investigation closure.'}</p>
            )}
            {sourceInvestigation?.closure?.what_was_done ? <div className="r42-memory-investigation-outcome"><strong>What was done</strong><p>{sourceInvestigation.closure.what_was_done}</p></div> : null}
            {sourceInvestigation?.closure?.what_happened_afterward ? <div className="r42-memory-investigation-outcome"><strong>What happened afterward</strong><p>{sourceInvestigation.closure.what_happened_afterward}</p></div> : null}
          </section>
        ) : null}

        {detail.whyItMatters ? (
          <section>
            <span className="r42-eyebrow">Why it matters</span>
            <Route42Markdown content={detail.whyItMatters} />
          </section>
        ) : null}

        {detail.relatedComponents.length ? (
          <section>
            <span className="r42-eyebrow">Related components</span>
            <div className="r42-memory-chip-list">{detail.relatedComponents.map((component) => <span key={component}>{component}</span>)}</div>
          </section>
        ) : null}

        {detail.limitations.length ? (
          <section>
            <span className="r42-eyebrow">Limitations / uncertainty</span>
            <ul className="r42-memory-detail-list">{detail.limitations.map((limitation) => <li key={limitation}><Route42InlineMarkdown content={limitation} /></li>)}</ul>
          </section>
        ) : null}

        <section>
          <span className="r42-eyebrow">Evidence references</span>
          <div className="r42-memory-reference-grid">
            {detail.documentIds.length ? <div><strong>{detail.documentIds.length}</strong><span>Documents</span></div> : null}
            {detail.factCount ? <div><strong>{detail.factCount}</strong><span>Facts</span></div> : null}
            {detail.chunkCount ? <div><strong>{detail.chunkCount}</strong><span>Knowledge chunks</span></div> : null}
            {detail.relationshipCount ? <div><strong>{detail.relationshipCount}</strong><span>Relationships</span></div> : null}
            {detail.sourceReferenceCount ? <div><strong>{detail.sourceReferenceCount}</strong><span>Investigation evidence refs</span></div> : null}
            {!detail.documentIds.length && !detail.factCount && !detail.chunkCount && !detail.relationshipCount && !detail.sourceReferenceCount ? <p className="r42-memory-reference-empty">No source-reference counts are attached to this Machine Memory item.</p> : null}
          </div>
        </section>

        {sourceDocuments.length ? (
          <section>
            <span className="r42-eyebrow">Source documents</span>
            <div className="r42-memory-source-list">
              {sourceDocuments.map((document) => (
                <Link key={document.document_id} to={`/machines/${machineId}/documents/${document.document_id}`}>
                  <i className="bi bi-file-earmark-text" aria-hidden="true" />
                  <span>{document.filename || document.document_id}</span>
                  <i className="bi bi-arrow-up-right" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {detail.sourceReferences.length ? (
          <section>
            <span className="r42-eyebrow">Supporting Investigation evidence</span>
            <div className="r42-memory-investigation-evidence-list">
              {detail.sourceReferences.map((reference) => {
                const document = reference.document_id
                  ? documents.find((value) => value.document_id === reference.document_id)
                  : undefined;
                return (
                  <div key={`${reference.artifact_type}:${reference.artifact_id}:${reference.document_id ?? ''}`}>
                    <span><strong>{humanMemoryType(reference.artifact_type) || 'Evidence'}</strong><small>{reference.artifact_id}</small></span>
                    {document ? <Link to={`/machines/${machineId}/documents/${document.document_id}`}>{document.filename || document.document_id} <i className="bi bi-arrow-up-right" aria-hidden="true" /></Link> : null}
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}
      </div>

      <footer className="r42-memory-detail-actions">
        {onAsk ? <button className="btn btn-outline-secondary" type="button" onClick={() => onAsk(detail.prompt)}>Ask about this</button> : null}
        {onInvestigate ? <button className="btn r42-btn-primary" type="button" onClick={() => onInvestigate(detail.investigationTitle, detail.investigationObjective)}>Investigate</button> : null}
      </footer>
    </aside>
  );
}

/** Use the same grounded-question prompt from Machine Memory rows and the detail drawer. */
export function memoryQuestionFor(item: MachineMemoryItem): string {
  return normalize(item).prompt;
}

type NormalizedDetail = {
  title: string;
  statement: string;
  kindLabel: string;
  strength: string | null;
  whyItMatters: string | null;
  limitations: string[];
  relatedComponents: string[];
  documentIds: string[];
  factCount: number;
  chunkCount: number;
  relationshipCount: number;
  sourceReferenceCount: number;
  sourceReferences: MachineMemoryKnowledgeEvidenceDto[];
  investigationId: string | null;
  establishedAt: string | null;
  prompt: string;
  investigationTitle: string;
  investigationObjective: string;
};

function normalize(item: MachineMemoryItem): NormalizedDetail {
  if (item.kind === 'INSIGHT') return fromInsight(item.value);
  if (item.kind === 'RELATIONSHIP') return fromRelationship(item.value);
  if (item.kind === 'GAP') return fromGap(item.value);
  if (item.kind === 'CONFLICT') return fromConflict(item.value);
  if (item.kind === 'INVESTIGATION_KNOWLEDGE') return fromInvestigationKnowledge(item.value);
  return fromTheme(item.value);
}

function fromInsight(value: MachineMemoryInsightDto): NormalizedDetail {
  const title = value.title || humanMemoryType(value.type) || 'Machine insight';
  const statement = value.statement || title;
  return {
    title,
    statement,
    kindLabel: humanMemoryType(value.type) || 'Insight',
    strength: evidenceStrengthLabel(value.evidence_strength),
    whyItMatters: value.why_it_matters || null,
    limitations: value.limitations ?? [],
    relatedComponents: [],
    documentIds: value.supporting_document_ids ?? [],
    factCount: value.supporting_fact_ids?.length ?? 0,
    chunkCount: value.supporting_knowledge_chunk_ids?.length ?? 0,
    relationshipCount: value.supporting_relationship_ids?.length ?? 0,
    sourceReferenceCount: 0,
    sourceReferences: [],
    investigationId: null,
    establishedAt: null,
    prompt: `What should I understand about this Machine Memory insight: ${title}?`,
    investigationTitle: trimTitle(title),
    investigationObjective: `Determine what the grounded machine evidence supports about this Machine Memory insight: ${statement}`.slice(0, 1200),
  };
}

function fromRelationship(value: MachineMemoryRelationshipDto): NormalizedDetail {
  const title = value.title || humanMemoryType(value.type) || 'Observed relationship';
  const statement = value.statement || title;
  const supporting = value.supporting_evidence ?? {};
  const conflicting = value.conflicting_evidence ?? {};
  const limitations = [value.uncertainty_notes, hasAny(conflicting) ? 'Conflicting evidence is attached to this relationship.' : null].filter((value): value is string => Boolean(value));
  return {
    title,
    statement,
    kindLabel: humanMemoryType(value.type) || 'Observed Relationship',
    strength: evidenceStrengthLabel(hasAny(conflicting) ? 'CONFLICTING' : value.evidence_strength),
    whyItMatters: null,
    limitations,
    relatedComponents: value.related_components ?? [],
    documentIds: unique([...(supporting.document_ids ?? []), ...(conflicting.document_ids ?? [])]),
    factCount: (supporting.fact_ids?.length ?? 0) + (conflicting.fact_ids?.length ?? 0),
    chunkCount: (supporting.knowledge_chunk_ids?.length ?? 0) + (conflicting.knowledge_chunk_ids?.length ?? 0),
    relationshipCount: 0,
    sourceReferenceCount: 0,
    sourceReferences: [],
    investigationId: null,
    establishedAt: null,
    prompt: `Explain the evidence behind this observed relationship: ${title}.`,
    investigationTitle: trimTitle(title),
    investigationObjective: `Determine how strongly the grounded machine evidence supports this observed relationship: ${statement}`.slice(0, 1200),
  };
}

function fromGap(value: MachineMemoryGapDto): NormalizedDetail {
  const title = humanMemoryType(value.gap_type) || 'Knowledge gap';
  const statement = value.statement || title;
  return {
    title,
    statement,
    kindLabel: 'Knowledge Gap',
    strength: 'Limited',
    whyItMatters: null,
    limitations: value.limitations ?? [],
    relatedComponents: [],
    documentIds: value.supporting_document_ids ?? [],
    factCount: 0,
    chunkCount: 0,
    relationshipCount: value.supporting_relationship_ids?.length ?? 0,
    sourceReferenceCount: 0,
    sourceReferences: [],
    investigationId: null,
    establishedAt: null,
    prompt: `What information would help close this Machine Memory knowledge gap: ${statement}`,
    investigationTitle: trimTitle(`Resolve ${title}`),
    investigationObjective: `Determine what evidence or verification is needed to resolve this Machine Memory gap: ${statement}`.slice(0, 1200),
  };
}

function fromConflict(value: MachineMemoryConflictDto): NormalizedDetail {
  const title = humanMemoryType(value.conflict_type) || 'Conflicting information';
  const statement = value.statement || title;
  return {
    title,
    statement,
    kindLabel: 'Conflicting Information',
    strength: 'Conflicting',
    whyItMatters: value.field ? `The conflict affects the machine field “${value.field}”.` : null,
    limitations: [],
    relatedComponents: [],
    documentIds: value.document_ids ?? [],
    factCount: value.fact_ids?.length ?? 0,
    chunkCount: value.knowledge_chunk_ids?.length ?? 0,
    relationshipCount: 0,
    sourceReferenceCount: 0,
    sourceReferences: [],
    investigationId: null,
    establishedAt: null,
    prompt: `Help me understand and resolve this conflicting Machine Memory information: ${statement}`,
    investigationTitle: trimTitle(`Resolve ${title}`),
    investigationObjective: `Determine which grounded evidence should be trusted and how this Machine Memory conflict should be resolved: ${statement}`.slice(0, 1200),
  };
}

function fromTheme(value: MachineMemoryThemeDto): NormalizedDetail {
  const title = value.title || 'Recurring theme';
  const statement = value.statement || title;
  return {
    title,
    statement,
    kindLabel: 'Recurring Theme',
    strength: evidenceStrengthLabel(value.evidence_strength),
    whyItMatters: null,
    limitations: [],
    relatedComponents: [],
    documentIds: value.supporting_document_ids ?? [],
    factCount: 0,
    chunkCount: 0,
    relationshipCount: 0,
    sourceReferenceCount: 0,
    sourceReferences: [],
    investigationId: null,
    establishedAt: null,
    prompt: `What does the evidence say about this recurring machine theme: ${title}?`,
    investigationTitle: trimTitle(title),
    investigationObjective: `Determine what the grounded machine evidence supports about this recurring theme: ${statement}`.slice(0, 1200),
  };
}

function fromInvestigationKnowledge(value: MachineMemoryInvestigationKnowledgeDto): NormalizedDetail {
  const statement = value.statement || humanMemoryType(value.knowledge_type) || 'Confirmed Investigation learning';
  const evidence = value.supporting_evidence ?? [];
  return {
    title: humanMemoryType(value.knowledge_type) || 'Investigation learning',
    statement,
    kindLabel: 'Learned from Investigation',
    strength: null,
    whyItMatters: 'This is durable machine knowledge that a human explicitly confirmed when an Investigation closed. It describes a prior established learning, not proof that the same condition is occurring now.',
    limitations: [],
    relatedComponents: [],
    documentIds: unique(evidence.map((reference) => reference.document_id || '').filter(Boolean)),
    factCount: evidence.filter((reference) => reference.artifact_type.toLocaleUpperCase() === 'FACT').length,
    chunkCount: evidence.filter((reference) => reference.artifact_type.toLocaleUpperCase().includes('KNOWLEDGE')).length,
    relationshipCount: evidence.filter((reference) => reference.artifact_type.toLocaleUpperCase().includes('RELATIONSHIP')).length,
    sourceReferenceCount: evidence.length,
    sourceReferences: evidence,
    investigationId: value.investigation_id,
    establishedAt: value.closure_timestamp || null,
    prompt: `What did the prior Investigation establish about this machine: ${statement}?`,
    investigationTitle: trimTitle(`Revisit ${humanMemoryType(value.knowledge_type) || 'prior learning'}`),
    investigationObjective: `Determine whether current machine evidence shows a recurrence or new implication of this prior human-confirmed learning: ${statement}`.slice(0, 1200),
  };
}

function trimTitle(value: string): string {
  const clean = value.replace(/\s+/g, ' ').trim();
  return clean.length <= 80 ? clean : `${clean.slice(0, 77).trimEnd()}…`;
}

function unique(values: string[]) { return [...new Set(values.filter(Boolean))]; }
function formatDate(value: string) { const date = new Date(value); return Number.isNaN(date.valueOf()) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date); }
function hasAny(value: MachineMemoryRelationshipEvidenceDto) {
  return Boolean(value.document_ids?.length || value.document_summary_ids?.length || value.fact_ids?.length || value.knowledge_chunk_ids?.length);
}
